/// <reference types="vite/client" />
import React from "react";
import { createRoot } from "react-dom/client";
import isPropValid from "@emotion/is-prop-valid";
import { Provider } from "react-redux";
import { createInertiaApp } from "@inertiajs/react";
import { GlobalTheme, GloablStyledComponent } from "./styles";
import { StyleSheetManager, ThemeProvider } from "styled-components";
import { AppStore } from "./redux-state";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import "../css/app.css";
import "../css/imported-design-components.css";
import setupLocatorUI from "@locator/runtime";
import InitFromProps from "./InitFromProps";
import { DevTools } from "jotai-devtools";
import { startInstallPromptCapture } from "./pwa/installPrompt";

if (process.env.NODE_ENV === "development") {
    setupLocatorUI();
}

/**
 * Before React, deliberately. Chrome fires `beforeinstallprompt` as soon as it has parsed the
 * document and the manifest, which is routinely earlier than the first mount — a listener
 * registered in an effect misses it on most cold loads and looks like a browser that never fires.
 */
startInstallPromptCapture();

const appName =
    window.document.getElementsByTagName("title")[0]?.innerText || "Laravel";

const rootElement: any = document.getElementById("inertia-app");
const root = createRoot(rootElement);
createInertiaApp({
    progress: { color: "#4B5563" },
    title: (title) => (!!title ? `${title} · ${appName}` : `${appName}`),
    resolve: async (name) => {
        const pages = import.meta.glob([
            "./Pages/**/*.tsx",
            "!./Pages/**/*.test.tsx",
            "!./Pages/**/*.spec.tsx",
            "!./Pages/**/*.stories.tsx",
            "!./Pages/**/tests/**",
            "!./Pages/**/__tests__/**",
        ]);

        const directPath = `./Pages/${name}.tsx`;
        const indexPath = `./Pages/${name}/index.tsx`;
        const path = directPath in pages ? directPath : indexPath;

        const page = (await resolvePageComponent(path, pages)) as any;
        page.default.layout =
            page.default.layout ?? ((children: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined) => <InitFromProps children={children} />);

        return page;
    },
    setup({ el, App, props }) {
        return root.render(
            <Provider store={AppStore}>
                <StyleSheetManager
                    shouldForwardProp={(prop) => {
                        return isPropValid(prop);
                    }}
                >
                    <DevTools />
                    <GloablStyledComponent />
                    <ThemeProvider theme={GlobalTheme}>
                        <App {...props} />
                    </ThemeProvider>
                </StyleSheetManager>
            </Provider>,
        );
    },
});
